// src/utils/khaata.js
// D-289 Phase 1c — vendor & subcontractor KHAATA (running account) maths.
// D-289 Step 2 (07 Oct 2026) — "Choose bills" allocations + payment preview.
//
// WHY THIS EXISTS
// On the ground, vendors and subcontractors are paid against their running
// account, not bill by bill: "₹5 lakh billed, ₹3 lakh paid, ₹2 lakh due".
// This file turns ledger rows into that account. It is PURE (no Firestore),
// so the same maths can be tested and reused by any screen.
//
// WHERE THE NUMBERS COME FROM (all from financial_transactions — nothing is
// stored as a balance anywhere, so it can never go stale):
//   BILLS
//     vendor        → rows with type "vendor" (GRN / Direct Site finalize),
//                     party = row.vendor_id (vendors collection doc id)
//     subcontractor → rows with type "expense" + reference_type
//                     "subcontractor_work"; these rows carry no party id, so
//                     the party comes from the work log (reference_id) →
//                     log.subcontractor_id
//   PAYMENTS
//     new (D-289)   → rows with type "payment" + party_type/party_id. May
//                     carry allocations: [{ bill_id, amount }] when the user
//                     chose which bills the payment is for.
//     old (before D-289) → a bill row whose own status was edited to "paid"
//                     counts as a payment of THAT bill, of paid_amount (or
//                     amount). Detect-and-fallback: old rows are never edited.
//
// HOW BILLS ARE SETTLED — always in this order, so the result never depends
// on load order or on which screen shows it:
//   1. Explicit allocations (chosen bills, and legacy "paid" rows) settle the
//      bill they name, capped at what is still due on that bill. Any part
//      that cannot be applied is not lost — it joins the pool.
//   2. The pool (everything not explicitly allocated) settles the OLDEST
//      bill first.
//   3. Whatever is left after every bill is paid is an ADVANCE.
// A bill's status (Paid / Partly paid / Due) is therefore CALCULATED, never
// stored.
//
// OPENING BALANCES (D-296, 09 Oct 2026) — type "opening_balance",
// entry_kind "party": what was owed (side "due") or paid in advance
// (side "advance") on the go-live date. A due is the party's oldest bill
// (dated by as_of), so payments settle it first; an advance works like an
// earlier payment. A reversal row (entry_kind "reversal", reverses_id) drops
// both rows. Opening rows never touch Income / Expense or the Cash Book.

import { reversedOpeningIds } from "./cashBook";

export const UNASSIGNED = "__unassigned__";

// "YYYY-MM-DD" → local midnight in ms (0 if missing / invalid).
const asOfMs = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime() : 0;
};

const num   = (v) => Number(v || 0);
const r2    = (n) => Math.round(n * 100) / 100;
const msOf  = (ts) => (ts && typeof ts.toMillis === "function") ? ts.toMillis()
                    : (ts && ts.seconds) ? ts.seconds * 1000 : 0;

// Keeps only usable allocation lines: a bill id and an amount above zero.
const cleanAllocations = (list) =>
  (Array.isArray(list) ? list : [])
    .map((a) => ({ bill_id: String(a?.bill_id || ""), amount: r2(num(a?.amount)) }))
    .filter((a) => a.bill_id && a.amount > 0);

// THE settlement rule (see header). bills: [{ id, amount, at }],
// payments: [{ gross, allocations }]. Returns how much of each bill is
// settled, and the advance left over. Pure — used by buildKhaatas() and by
// previewPartyPayment(), so the preview can never disagree with the khaata.
export function settleBills(bills = [], payments = []) {
  const remaining = {};
  for (const b of bills) remaining[b.id] = r2(num(b.amount));

  // 1. Explicit allocations
  let pool = 0;
  for (const p of payments) {
    const gross = r2(num(p.gross));
    let used = 0;
    for (const a of cleanAllocations(p.allocations)) {
      if (!(a.bill_id in remaining)) continue;            // not this party's bill → pool
      const applied = r2(Math.min(a.amount, Math.max(remaining[a.bill_id], 0), Math.max(r2(gross - used), 0)));
      if (applied <= 0) continue;
      remaining[a.bill_id] = r2(remaining[a.bill_id] - applied);
      used = r2(used + applied);
    }
    pool = r2(pool + Math.max(r2(gross - used), 0));
  }

  // 2. Pool — oldest bill first
  const oldestFirst = [...bills].sort((x, y) => x.at - y.at);
  for (const b of oldestFirst) {
    const take = r2(Math.min(Math.max(remaining[b.id], 0), Math.max(pool, 0)));
    if (take <= 0) continue;
    remaining[b.id] = r2(remaining[b.id] - take);
    pool = r2(pool - take);
  }

  const settledByBill = {};
  for (const b of bills) settledByBill[b.id] = r2(num(b.amount) - remaining[b.id]);
  return { settledByBill, advance: r2(Math.max(pool, 0)) };
}

const stateOf = (amount, settled) => {
  const due = r2(amount - settled);
  return due <= 0 ? "paid" : settled > 0 ? "partly" : "due";
};

export function buildKhaatas({ ledgerRows = [], logsById = {}, vendorsById = {}, subcontractorsById = {} }) {
  const parties = new Map();

  const partyFor = (type, id, nameHint) => {
    const pid = id || UNASSIGNED;
    const key = `${type}:${pid}`;
    if (!parties.has(key)) {
      const master = type === "vendor" ? vendorsById[pid] : subcontractorsById[pid];
      const masterName = type === "vendor" ? master?.vendor_name : master?.name;
      parties.set(key, {
        key,
        party_type:    type,
        party_id:      pid === UNASSIGNED ? "" : pid,
        name:          masterName || nameHint ||
                       (pid === UNASSIGNED ? "Not linked to a vendor" : "Unknown"),
        is_unassigned: pid === UNASSIGNED,
        in_master:     !!master,
        bills:         [],
        payments:      [],
      });
    }
    return parties.get(key);
  };

  // Legacy (before D-289): the bill row itself was edited to "paid". It is a
  // payment of THAT bill, so it is an explicit allocation to it.
  const legacyPaymentFrom = (row, amount) => ({
    id:           `legacy_${row.id}`,
    at:           msOf(row.paid_at || row.updated_at || row.created_at),
    gross:        amount,
    tds:          0,
    net:          amount,
    mode:         row.payment_mode || "",
    reference_no: row.notes || "",
    by:           row.paid_by_name || "",
    allocations:  [{ bill_id: row.id, amount }],
    legacy:       true,
  });

  const reversedIds = reversedOpeningIds(ledgerRows);

  for (const row of ledgerRows) {
    // ── OPENING BALANCE (D-296) ──
    if (row.type === "opening_balance") {
      if (row.entry_kind !== "party" || reversedIds.has(row.id)) continue;
      if (row.party_type !== "vendor" && row.party_type !== "subcontractor") continue;
      const amount = num(row.amount);
      if (!(amount > 0)) continue;
      const p  = partyFor(row.party_type, row.party_id, row.party_name);
      const at = asOfMs(row.as_of) || msOf(row.created_at);
      if (row.side === "advance") {
        p.payments.push({
          id: row.id, at, gross: amount, tds: 0, net: amount,
          mode: "", reference_no: "", notes: row.notes || "",
          by: row.created_by_name || "", allocations: [],
          legacy: false, opening: true,
        });
      } else {
        p.bills.push({
          id: row.id, at, amount, ref: "Opening balance",
          source_id: "", project_name: "", opening: true,
        });
      }
      continue;
    }

    // ── VENDOR BILL ──
    if (row.type === "vendor") {
      const p = partyFor("vendor", row.vendor_id, row.vendor_name);
      p.bills.push({
        id:           row.id,
        at:           msOf(row.created_at),
        amount:       num(row.amount),
        ref:          row.po_number || row.reference_no || "",
        source_id:    row.purchase_order_id || row.reference_id || "",
        project_name: row.project_name || "",
      });
      if (row.status === "paid") p.payments.push(legacyPaymentFrom(row, num(row.paid_amount || row.amount)));
      continue;
    }

    // ── SUBCONTRACTOR BILL ──
    if (row.type === "expense" && row.reference_type === "subcontractor_work") {
      const log = logsById[row.reference_id];
      const p = partyFor("subcontractor", log?.subcontractor_id, log?.subcontractor_name);
      p.bills.push({
        id:           row.id,
        at:           msOf(row.created_at),
        amount:       num(row.amount),
        ref:          log?.log_date ? `Work ${log.log_date}` : (row.description || ""),
        source_id:    row.reference_id || "",
        project_name: row.project_name || log?.project_name || "",
      });
      if (row.status === "paid") p.payments.push(legacyPaymentFrom(row, num(row.amount)));
      continue;
    }

    // ── PAYMENT (D-289) ──
    if (row.type === "payment" && (row.party_type === "vendor" || row.party_type === "subcontractor")) {
      const p = partyFor(row.party_type, row.party_id, row.party_name);
      p.payments.push({
        id:           row.id,
        at:           msOf(row.created_at),
        gross:        num(row.gross_amount),
        tds:          num(row.tds_amount),
        net:          num(row.amount),
        mode:         row.payment_mode || "",
        reference_no: row.reference_no || "",
        notes:        row.notes || "",
        by:           row.paid_by_name || "",
        allocations:  cleanAllocations(row.allocations),
        legacy:       false,
      });
    }
  }

  const list = [...parties.values()].map((p) => {
    p.bills.sort((a, b) => a.at - b.at);
    p.payments.sort((a, b) => a.at - b.at);

    const billed    = r2(p.bills.reduce((s, b) => s + b.amount, 0));
    const settled   = r2(p.payments.reduce((s, x) => s + x.gross, 0));
    const paid_cash = r2(p.payments.reduce((s, x) => s + x.net, 0));
    const tds_total = r2(p.payments.reduce((s, x) => s + x.tds, 0));

    const { settledByBill } = settleBills(p.bills, p.payments);
    for (const b of p.bills) {
      b.settled = settledByBill[b.id];
      b.due     = r2(b.amount - b.settled);
      b.state   = stateOf(b.amount, b.settled);
    }

    const balance = r2(billed - settled);
    return {
      ...p,
      billed, settled, paid_cash, tds_total, balance,
      due:     Math.max(balance, 0),
      advance: Math.max(-balance, 0),
    };
  });

  // Biggest due first, then advances, then by name.
  list.sort((a, b) => (b.due - a.due) || (b.advance - a.advance) || a.name.localeCompare(b.name));
  return list;
}

// What a NEW payment would do, BEFORE it is saved — the Record Payment
// preview ("this payment will settle: PO-x in full, PO-y ₹… left").
// party = one entry from buildKhaatas(). Same settleBills() rule as the
// khaata itself, so the preview and the saved result can never differ.
export function previewPartyPayment(party, { gross = 0, allocations = [] } = {}) {
  const amount = r2(num(gross));
  const before = {};
  for (const b of party.bills) before[b.id] = num(b.settled);

  const { settledByBill, advance } = settleBills(
    party.bills,
    [...party.payments, { gross: amount, allocations }]
  );

  const lines = party.bills
    .map((b) => ({
      bill_id:     b.id,
      ref:         b.ref,
      at:          b.at,
      amount:      b.amount,
      applied_now: r2(settledByBill[b.id] - before[b.id]),
      due_after:   r2(b.amount - settledByBill[b.id]),
      state_after: stateOf(b.amount, settledByBill[b.id]),
    }))
    .filter((l) => l.applied_now > 0);

  return {
    lines,
    advance_after: advance,
    due_after:     r2(Math.max(r2(party.balance - amount), 0)),
  };
}

// Bill order for the khaata screen: Partly paid first, then Due (oldest
// first — the order money is applied), then Paid (newest first).
const STATE_ORDER = { partly: 0, due: 1, paid: 2 };
export function billsForDisplay(bills = []) {
  return [...bills].sort((a, b) =>
    (STATE_ORDER[a.state] - STATE_ORDER[b.state]) ||
    (a.state === "paid" ? b.at - a.at : a.at - b.at)
  );
}

// One timeline for the khaata screen: bills and payments together, newest first.
export function timelineOf(party) {
  return [
    ...party.bills.map((b) => ({ kind: "bill", ...b })),
    ...party.payments.map((x) => ({ kind: "payment", ...x })),
  ].sort((a, b) => b.at - a.at);
}